<template>
  <ValidationObserver v-if="isViewReady" ref="ads" tag="div" class="ads">
    <div class="ads__headerSection">
      <h2 class="mt-2">Leietakersøk - {{ rentalObject.details.alias || rentalObject.address.street }}</h2>

      <hl-Button v-if="!isRentalObjectCommercial" :href="pubomaticAppLink" target="_blank" rel="noopener">
        <div class="d-flex align-items-center">
          <Icon :name="IconName.InterfacePlus" :size="IconSize.Small" />
          <strong class="ml-2"> Opprett ny annonse </strong>
        </div>
      </hl-Button>
    </div>

    <!-- Decline Prospect Modal -->
    <hl-Modal
      v-if="isDeclineProspectModalShown"
      iconClass="budicon-sad-chat text-danger"
      title="Send avslag til valgt interessent"
      class="text-center"
    >
      <div>
        <p class="mb-5">
          Ved å klikke på “Avslag” sendes en e-post til {{ getProspectToStartAnAction.name }} hvor det informeres om at
          han/hun ikke er aktuell som leietaker for boligen.
        </p>

        <button class="btn btn-danger mx-2" @click="declineAndClose">Avslag</button>
        <button class="btn btn-outline-secondary mx-2" @click="toggleDeclineProspectModal">Avbryt</button>
      </div>
    </hl-Modal>
    <!-- / Decline Prospect Modal -->

    <!-- Add intressent modal -->
    <hl-Modal v-if="isNewCandidateModalShown" title="Registrer søker til boligen" class="text-center">
      <div>
        <div class="mb-5">
          <ValidationProvider name="Fullt navn" :rules="{ required: { allowFalse: false } }" v-slot="{ errors }">
            <hl-Input
              type="text"
              class="mt-section-3rd"
              v-model="candidateName"
              placeholder="Fullt navn"
              label="Fullt navn"
            />
            <div v-if="errors && errors[0]" class="error-message text-danger text-left">
              {{ errors[0] }}
            </div>
          </ValidationProvider>
          <ValidationProvider
            name="E-post"
            :rules="{ required: { allowFalse: false }, email: true }"
            v-slot="{ errors }"
          >
            <hl-Input
              type="email"
              class="mt-section-3rd"
              v-model="candidateEmail"
              placeholder="E-post"
              label="E-post"
            />
            <div v-if="errors && errors[0]" class="error-message text-danger text-left">
              {{ errors[0] }}
            </div>
          </ValidationProvider>
          <ValidationProvider name="Telefon" rules="required|valid-cell-phone" v-slot="{ errors }">
            <hl-Input
              type="text"
              class="mt-section-3rd"
              v-model="candidatePhone"
              placeholder="Telefon"
              label="Telefon"
            />
            <div v-if="errors && errors[0]" class="error-message text-danger text-left">
              {{ errors[0] }}
            </div>
          </ValidationProvider>
        </div>

        <button class="btn btn-primary mx-2" @click="createNewCandidate">Lagre</button>
        <button class="btn btn-outline-secondary mx-2" @click="toggleNewCandidateModal">Avbryt</button>
      </div>
    </hl-Modal>
    <!-- / Add intressent modal -->

    <hl-Modal v-if="isLoading" class="text-center" title="Siden laster">
      <hl-LoaderDots />
    </hl-Modal>

    <hl-Modal
      v-if="isVerificationModalVisible"
      class="text-center"
      title="BankID verifisering kreves"
      iconClass="budicon-announcement text-primary"
    >
      <p>
        Dette hjelper oss med å holde plattformen trygg og forhindre misbruk. Selv om du har verifisert deg tidligere,
        må vi bekrefte identiteten din ved hver publisering. Trykk på knappen under for å gå videre til verifisering.
      </p>

      <hl-Button class="mt-2" @click="createIdentificationSession"> Verifiser bruker </hl-Button>
    </hl-Modal>

    <hl-Modal
      v-if="isErrorModalVisible"
      class="text-center"
      title="En feil oppstod"
      iconClass="budicon-alert text-danger"
    >
      <p>
        {{ errorMessage }}
      </p>

      <div class="d-flex justify-content-center mt-5">
        <hl-Button @click="retryFinnAdPublish"> Forsøk igjen </hl-Button>
        <hl-Button variant="secondary" outline class="ml-3" @click="isErrorModalVisible = false"> Avbryt </hl-Button>
      </div>
    </hl-Modal>

    <hl-ModalV2
      v-if="isFinnPostingModalVisible"
      title="Publiser til FINN"
      title-icon="budicon-alert-sign"
      is-closable
      @close="isFinnPostingModalVisible = false"
    >
      <p>
        Ved å velge å publisere annonsen til FINN.no vil det faktureres <hl-Currency :amount="$options.FINN_PRICE" />.
        Dette vil være inkludert i neste faktura du mottar fra Husleie.no.
      </p>
      <hl-Checkbox v-model="areFinnTermsAgreed" size="sm">
        Jeg aksepterer FINNs
        <a
          href="https://hjelpesenter.finn.no/hc/no/articles/304234-Annonseregler-for-FINN-no"
          target="_blank"
          rel="noopener"
          class="text-primary"
        >
          annonseregler
        </a>
      </hl-Checkbox>

      <template #buttons>
        <hl-Button variant="primary" size="sm" :disabled="!areFinnTermsAgreed || isPublishingAd" @click="publishAd">
          Publiser
        </hl-Button>
      </template>
    </hl-ModalV2>

    <div class="ads__topInputs my-3">
      <select v-model="adFilter">
        <option v-for="option in adFilterOptions" :value="option.value" :key="option.value">
          {{ option.name }}
        </option>
      </select>

      <hl-Button v-if="!isRentalObjectCommercial" :href="pubomaticAppLink" target="_blank" rel="noopener">
        <div class="d-flex align-items-center">
          <Icon :name="IconName.InterfacePlus" :size="IconSize.Small" />
          <span class="ml-2"> Opprett ny annonse </span>
        </div>
      </hl-Button>
    </div>

    <InfoCard v-if="isRentalObjectCommercial">
      Det er ikke mulig å annonsere Næringseiendom. Hvis du ønsker
      å annonsere dette leieobjektet må det oppdateres til en annen boligtype.
    </InfoCard>

    <AdList
      v-if="sortedAds.length"
      :landlord-id="landlordId"
      :rental-object-id="rentalObjectId"
      :ads="sortedAds"
      class="mt-4"
      @publish-to-finn="handleFinnAdPublish"
    />

    <div v-else class="mt-5 text-center">
      <img src="@/assets/empty-ads.svg" />
      <h3>Du har ingen annonser</h3>
      <template v-if="adFilter === 'all'">
        <p>Oppretter du annonser via Husleie.no vil de vises her.</p>
        <hl-Button :href="`/app/pubomatic/${this.landlordId}/?rentalObjectId=${this.rentalObjectId}`" variant="primary">
          Opprett ny annonse
        </hl-Button>
      </template>
    </div>
  </ValidationObserver>
  <hl-Modal v-else class="text-center" title="Siden laster">
    <hl-LoaderDots />
  </hl-Modal>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import BulkProspectViewingForm from '@/components/detailedRentalObject/interestManagement/forms/viewings/BulkProspectViewingForm';
import RentalObjectSelectMenu from '@/components/rentalObjects/RentalObjectSelectMenu';
import AdList from '@/components/AdList.vue';
import Modal from '@modules/husleie-framework/src/components/modals/Modal.vue';
import LoaderDots from '@modules/husleie-framework/src/components/loaders/LoaderDots.vue';
import Currency from '@modules/husleie-framework/src/components/currency/Currency.vue';
import Input from '@modules/husleie-framework/src/components/forms/inputs/input/Input.vue';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import Icon, { IconName, IconSize } from '@modules/design-system/components/Icon';
import InfoCard from '@/components/InfoCard.vue';
import { LettingApi, InterestManagementApi, IdentificationApi } from '@modules/husleieapi2';
import { FINN_PRICE } from '@modules/husleie-framework/src/constants.ts';

export default Vue.extend({
  name: 'RentalObjectAds',
  components: {
    BulkProspectViewingForm,
    RentalObjectSelectMenu,
    AdList,
    'hl-Modal': Modal,
    'hl-LoaderDots': LoaderDots,
    'hl-Input': Input,
    'hl-Currency': Currency,
    'hl-Button': Button,
    InfoCard,
    Icon,
  },
  props: {
    landlordId: [Number, String],
    rentalObjectId: [Number, String],
  },
  data() {
    return {
      candidateName: '',
      candidateEmail: '',
      candidatePhone: '',
      adFilter: 'active',
      ads: [],
      isViewReady: false,
      identificationId: undefined,
      isVerificationModalVisible: false,
      isFinnPostingModalVisible: false,
      isErrorModalVisible: false,
      errorMessage: '',
      isLoading: false,
      areFinnTermsAgreed: false,
      isPublishingAd: false,
      finnPublishLettingId: undefined,
      IconName,
      IconSize,
    };
  },
  FINN_PRICE,
  computed: {
    ...mapGetters([
      'getCurrentIMId',
      'isExtendedRentalObjectLoaded',
      'isNewCandidateModalShown',
      'isDeclineProspectModalShown',
      'isViewingsModalShown',
      'isViewingsModalLoading',
      'getRentalObject',
      'getProspectToStartAnAction',
      'getIMIdToStartAnAction',
    ]),

    loaded() {
      return this.isExtendedRentalObjectLoaded;
    },
    adFilterOptions() {
      return [
        { name: 'Alle annonser', value: 'all' },
        { name: 'Aktive annonser', value: 'active' },
        { name: 'Avsluttede annonser', value: 'expired' },
      ];
    },
    rentalObject() {
      return this.getRentalObject(this.rentalObjectId);
    },
    isRentalObjectCommercial() {
      return this.rentalObject.details.type === 'commercial';
    },
    sortedAds() {
      const statusSortOrder = ['draft', 'active', 'pending', 'stopped', 'deleted'];

      return this.ads
        .filter(item => {
          return {
            all: true,
            active: item.letting.status.status === 'active',
            expired: item.letting.status.status === 'expired',
          }[this.adFilter];
        })
        .sort(
          (a, b) => statusSortOrder.indexOf(a.letting.status.status) - statusSortOrder.indexOf(b.letting.status.status)
        );
    },
    pubomaticAppLink() {
      return `/app/pubomatic/${this.landlordId}?rentalObjectId=${this.rentalObjectId}`;
    },
  },
  methods: {
    ...mapActions([
      'toggleDeclineProspectModal',
      'toggleViewingsModal',
      'toggleNewCandidateModal',
      'declineProspects',
      'getLandlordInterestManagements',
      'getLandlordInterestManagementCandidates',
      'postLandlordInterestManagementCandidate',
      'publishLetting',
    ]),

    async declineAndClose() {
      try {
        await this.declineProspects({
          landlordId: this.landlordId,
          iMId: this.getCurrentIMId,
          prospects: [this.getProspectToStartAnAction.id],
        });
        this.$toast('Avslag sendt.', 'success');
        this.getIMCandidates();
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke avslå interessent.', 'error');
      }
      this.toggleDeclineProspectModal();
    },
    async createNewCandidate() {
      const fieldsAreValid = await this.$refs.ads.validate();

      if (fieldsAreValid) {
        await this.postLandlordInterestManagementCandidate({
          landlordId: this.landlordId,
          iMId: this.getIMIdToStartAnAction,
          candidate: {
            name: this.candidateName,
            email: this.candidateEmail,
            phone: this.candidatePhone,
          },
        });
      }
    },
    async getIMCandidates() {
      try {
        this.getLandlordInterestManagementCandidates({
          landlordId: this.landlordId,
          iMId: this.iMId,
        });
      } catch (error) {
        console.log(error);
        this.$toast('Kunne ikke hente interessenter', 'error');
      }
    },
    async createIdentificationSession() {
      const api = new IdentificationApi();
      const res = await api.createIdentificationSession({
        body: {
          path: window.location.pathname,
        },
      });

      if (!res.url) {
        throw new Error('Missing field `url` from `createIdentificationSession` response');
      }

      window.localStorage.setItem('rentalObjectFinnPublishLettingId', this.finnPublishLettingId);
      window.location.href = res.url;
    },
    async handleFinnAdPublish(lettingId) {
      this.finnPublishLettingId = lettingId;

      if (this.identificationId) {
        this.isFinnPostingModalVisible = true;
      } else {
        this.isVerificationModalVisible = true;
      }
    },
    retryFinnAdPublish() {
      this.isErrorModalVisible = false;

      if (this.identificationId) {
        this.isFinnPostingModalVisible = true;
      } else {
        this.isVerificationModalVisible = true;
      }
    },
    async publishAd() {
      this.isPublishingAd = true;

      try {
        await this.publishLetting({
          identificationId: this.identificationId,
          landlordId: this.landlordId,
          lettingId: this.finnPublishLettingId,
          name: 'finn',
        });
        window.localStorage.removeItem('rentalObjectFinnPublishLettingId');
        window.location.href = window.location.pathname;
      } catch {
        this.isFinnPostingModalVisible = false;
        this.isErrorModalVisible = true;
        this.errorMessage = 'Publisering av annonsen feilet. Forsøk igjen om noen få minutter';
      } finally {
        this.isPublishingAd = false;
      }
    },
    async handleIdentificationAttempt() {
      const { externalReference } = this.$route.query;

      if (!externalReference) {
        return;
      }

      try {
        this.isLoading = true;

        const api = new IdentificationApi();
        const res = await api.getIdentificationSessionResult(externalReference);

        if (res.status === 'SUCCESS') {
          this.identificationId = externalReference;
          this.finnPublishLettingId = window.localStorage.getItem('rentalObjectFinnPublishLettingId');

          this.isLoading = false;
          this.isFinnPostingModalVisible = true;
        } else {
          this.isLoading = false;
          this.isErrorModalVisible = true;
          this.errorMessage = 'BankID verifisering ble avbrutt. Vennligst forsøk igjen.';
        }
      } catch {
        this.isLoading = false;
        this.isErrorModalVisible = true;
        this.errorMessage = 'Annonsen kunne ikke lastes opp, forsøk igjen om noen få minutter.';
      }
    },
    async getInterestManagementData() {
      let interestManagements = [];
      // Might be duplicate ads through unique interest managements.
      const adIds = [];

      try {
        const api = new InterestManagementApi();
        interestManagements = await api.getLandlordInterestManagements(this.landlordId, {
          query: {
            rentalObjectId: this.rentalObjectId,
          },
        });
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke hente data.', 'error');
      }

      if (!interestManagements || !interestManagements.length) {
        return;
      }

      const data = interestManagements.map(async iM => {
        if (adIds.includes(iM.ad.id)) {
          return undefined;
        }

        adIds.push(iM.ad.id);

        // Using a try catch block here for `getLetting` as some ads might have data
        // quality issues, leading to a 500 openapi error.
        try {
          const api = new LettingApi();
          const letting = await api.getLetting(this.landlordId, iM.ad.id);
          return {
            iM,
            letting,
          };
        } catch {
          return undefined;
        }
      });

      this.ads = (await Promise.all(data)).filter(item => item);
    },
  },
  async created() {
    await this.getInterestManagementData();
    await this.handleIdentificationAttempt();

    if (['all', 'active', 'expired'].includes(this.$route.query.filter)) {
      this.adFilter = this.$route.query.filter;
    }

    this.isViewReady = true;
  },
});
</script>

<style lang="scss" scoped>
.ads__headerSection {
  display: flex;
  justify-content: space-between;
}
select {
  font-size: 1rem;
  padding: 0.8rem 1rem;
  background-color: white;
  border: 1px solid $gray-400;
  border-radius: 8px;
  width: 100%;
  outline: 0;
}
.ads__headerSection a {
  display: none;
}
.ads__topInputs {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem 0rem;
}
.ads__topInputs a {
  display: initial;
}

@media (min-width: 470px) {
  .ads__topInputs {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0;
  }
  .ads__topInputs * {
    width: fit-content;
  }
}

@media (min-width: 1200px) {
  .ads__headerSection a {
    display: initial;
    height: fit-content;
  }
  .ads__topInputs a {
    display: none;
  }
}
</style>
